/** One piece of a consent at the version shown: its checkbox text, notice, or a linked document (KOR-15237). */
export interface WorkflowConsentContent {
  documentId: string;
  version: string;
  language?: string;
  title?: string;
  /** Plain text: render it as text, never as HTML. */
  text?: string;
  /** Signed, temporary link. */
  url?: string;
}

/** A consent the customer still owes on the landing page or a step: one checkbox, which can cover several documents. */
export interface WorkflowConsent {
  consentDefinitionId: string;
  displayOrder?: number;
  /** `false` is optional: shown, but it never keeps Start disabled. Absent means required. */
  required?: boolean;
  /** Enforced by KOR-15245; shown as a normal consent until then. */
  requiresDocumentOpen?: boolean;
  checkboxText: WorkflowConsentContent;
  notice?: WorkflowConsentContent;
  documents?: WorkflowConsentContent[];
  /** Sent back unchanged when recording: it proves which versions were shown. */
  presentationToken: string;
  presentationExpiresAt?: string;
}

/** A ticked consent as it's sent back to be recorded: exactly the versions shown, and the token that proves it. */
export interface ShownConsent {
  consentDefinitionId: string;
  presentationToken: string;
  language?: string;
  checkboxText: { documentId: string; version: string };
  notice?: { documentId: string; version: string };
  documents: { documentId: string; version: string }[];
}

const shownVersion = ({ documentId, version }: WorkflowConsentContent): { documentId: string; version: string } => ({
  documentId,
  version,
});

export const toShownConsent = ({
  consentDefinitionId,
  presentationToken,
  checkboxText,
  notice,
  documents,
}: WorkflowConsent): ShownConsent => ({
  consentDefinitionId,
  presentationToken,
  language: checkboxText.language,
  checkboxText: shownVersion(checkboxText),
  ...(notice ? { notice: shownVersion(notice) } : {}),
  documents: (documents ?? []).map(shownVersion),
});
